import { createUseStyles } from 'react-jss';
import React from 'react';

import type { IconVariant } from '../Icon';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import type { LcpnTheme } from '../../styles/theme';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  filterOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minHeight: 'auto',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0, 4, 4),
    flex: 1,
  },

  nestedFilterOption: {
    '&&': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
      padding: theme.spacing(2, 0, 4, 4),
      minHeight: 'auto',
      height: 'fit-content',
      marginBottom: theme.spacing(-1),
    },
  },

  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },

  textGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: theme.spacing(25),
    minHeight: theme.spacing(10),
  },

  group: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
  },

  [`@media screen and (max-width: ${theme.breakPoints.desktop}px)`]: {
    filterOption: {
      '&&': {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(4),
        minHeight: theme.spacing(1),
        padding: theme.spacing(2),
      },
    },
    nestedFilterOption: {
      '&&': {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(4),
        minHeight: theme.spacing(1),
        padding: theme.spacing(2),
      },
    },
    group: {
      '&&': {
        flexDirection: 'row',
        alignItems: 'center',
        '& > img': {
          width: theme.spacing(5),
          height: theme.spacing(5),
        },
      },
    },
    buttonGroup: {
      '&&': {
        marginLeft: 0,
        width: '100%',
        marginRight: 0,
        flexWrap: 'wrap',
      },
    },
    textGroup: {
      '&&': {
        minHeight: 'auto',
        marginLeft: theme.spacing(2),
        '& > div > p': {
          fontSize: theme.fontSizes.p3,
        },
      },
    },
  },

  [`@media screen and (max-width: ${theme.breakPoints.tablet}px)`]: {
    filterOption: {
      '&&': {
        padding: theme.spacing(2, 4),
      },
    },
    nestedFilterOption: {
      '&&': {
        padding: theme.spacing(2, 0, 2, 4),
      },
    },
  },
}));

type Props = {
  icon: IconVariant;
  title: string;
  subtitle?: string;
  children: JSX.Element | JSX.Element[];
};

export function FilterOption(props: Props) {
  const classes = useStyles();
  const { icon = 'Target', title, subtitle, children } = props;
  return (
    <div className={classes.filterOption}>
      <div className={classes.group}>
        <Icon variant={icon} width={20} height={20} />
        <div className={classes.textGroup}>
          <Typography variant="p2">{title}</Typography>
          <Typography variant="p3">{subtitle}</Typography>
        </div>
      </div>
      <div className={classes.buttonGroup}>{children}</div>
    </div>
  );
}

export function NestedFilterOption(props: Omit<Props, 'subtitle'>) {
  const classes = useStyles();
  const { icon = 'Target', title, children } = props;
  return (
    <div className={classes.filterOption}>
      <div className={classes.group}>
        <Icon variant={icon} width={20} height={20} />
        <div className={classes.textGroup}>
          <Typography variant="p2">{title}</Typography>
        </div>
      </div>
      <div className={classes.buttonGroup}>{children}</div>
    </div>
  );
}
